/** @prettier */

import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
} from '@mui/material';
import {useEffect, useState} from 'react';
import {useParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';

//MTH component import
import NoMoreRegister from '../../component/NoMoreRegister';
import {ChkInputTextbox} from '../../component/ChkInputTextbox';
import {SLIUAddressSelectBox} from '../../component/SelectBox';
import SLIUUnitTypeSelectBox from '../sl-iu-registration/SLIUUnitTypeSelectBox';
import APIErrorMessage from '../../component/APIErrorMessage';
import {
  PrimaryButton,
  AddButton,
  SecondaryButton,
} from '../../component/BasicButton';
//API import
import {
  patchCompositionsSLUnit,
  getCompositionsSLUnitByOUID,
  getCompositionsSLUnit,
  getCompositionsSLIFUnit,
} from '../../API/API_Collection';
//interface import
import * as apiIF from '../../Type/api';
//Style import
import '../../css/common/common.css';

import {sortByUnicode} from '../../functions/sort';
import {format, limit} from '../../define';

/**
 * 室内機追加ダイアログ
 * @param props props
 * @returns JSX.Element
 */
const IUAddDialog = (props: {
  /** 室内機追加ボタンの名前 */
  addButtonName: string;
  /** 室外機の情報 */
  ouInfo: apiIF.res.getCompositionsSLUnitByOUID;
  /** 室外機の情報（set関数） */
  setOUInfo: React.Dispatch<
    React.SetStateAction<apiIF.res.getCompositionsSLUnitByOUID | null>
  >;
  /** 課金しているかどうか */
  isBilling: boolean;
  /** GW毎の構成情報未反映の有無（set関数） */
  setHasConfigGW: React.Dispatch<React.SetStateAction<boolean>>;
  /** SL新旧設定 */
  slType: 'new' | 'old';
}) => {
  //パスパラメータ取得
  const {siteID, gwID, systemType: slType, ouID} = useParams();

  const [iuName, setIUName] = useState(''); //室内機の名前
  const [iuAddress, setIUAddress] = useState(''); //SLアドレス
  //prettier-ignore
  const [unitType, setUnitType] = useState<apiIF.res.getCompositionsSLUnitByOUID['indoor-unit-informations'][0]['indoor-unit-type']>('FDT'); //室内機タイプ
  const [isOpenDialog, setIsOpenDialog] = useState(false); //ダイアログ表示フラグ（追加ダイアログ・登録超過ダイアログ）
  const [okBtnDisabled, setOKBtnDisabled] = useState(false); //OKボタン非表示フラグ
  const [iuNumber, setIUNumber] = useState(0); //室内機台数
  const [isOpenErrorDialog, setIsOpenErrorDialog] = useState(false); //APIエラー時エラーメッセージ表示
  const [apiWait, setAPIWait] = useState(false); //API応答待ちフラグ

  //TextBoxのエラー状態
  const [iuNameError, setIUNameError] = useState(false); //室内機の名前

  //入力項目のチェック
  useEffect(() => {
    if (iuName === '' || iuNameError || apiWait) {
      setOKBtnDisabled(true);
    } else {
      setOKBtnDisabled(false);
    }
  }, [iuName, iuNameError, apiWait]);

  //登録台数を算出
  useEffect(() => {
    //パスパラメータを取得できない場合
    if (siteID === undefined || gwID === undefined || slType === undefined) {
      return;
    }

    const promises = [];
    //室内外機の情報を取得
    promises.push(
      getCompositionsSLUnit(siteID, gwID, {
        'fields': 'sl-system,indoor-unit-informations',
        'max-result': 400,
      })
    );
    //その他の空調機の情報を取得
    promises.push(
      getCompositionsSLIFUnit(siteID, gwID, {
        'fields': 'sl-system',
        'max-result': 400,
      })
    );

    Promise.all(promises).then(response => {
      const slUnitInfo = response[0] as apiIF.res.getCompositionsSLUnit;
      const slIFUnitInfo = response[1] as apiIF.res.getCompositionsSLIFUnit;

      //表示している室内機と同じ系統番号のみカウントする
      let num = 0;
      for (const ouInfo of slUnitInfo['outdoor-unit-informations']) {
        if (
          ouInfo['sl-system'] === slType.slice(2) &&
          ouInfo['indoor-unit-informations'] !== undefined
        ) {
          num += ouInfo['indoor-unit-informations'].length;
        }
      }
      for (const unitInfo of slIFUnitInfo['unit-informations']) {
        if (unitInfo['sl-system'] === slType.slice(2)) {
          num++;
        }
      }
      setIUNumber(num);
    });
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.ouInfo]);

  /**
   * ADD indoor unitボタン押下時処理
   */
  const handleClickOpen = () => {
    setIsOpenDialog(true);
  };

  /**
   * OKボタン押下時処理
   */
  const addIU = () => {
    //パスパラメータを取得できない場合
    //室外機情報を取得できない場合
    if (
      siteID === undefined ||
      gwID === undefined ||
      ouID === undefined ||
      props.ouInfo === null
    ) {
      return;
    }

    setAPIWait(true);

    //APIに送信するデータを作成
    //追加のため室内機IDは指定しない
    const addIUData = {
      'indoor-unit-name': iuName,
      'sl-address': iuAddress,
      'indoor-unit-type': unitType,
    };
    const editData: apiIF.reqParameters.patchCompositionsSLUnit = {
      'indoor-unit-informations': [
        ...props.ouInfo['indoor-unit-informations'],
        addIUData,
      ],
    };
    //情報を編集
    patchCompositionsSLUnit(siteID, gwID, ouID, editData)
      .then(() => {
        //室内外機の情報を取得
        return getCompositionsSLUnitByOUID(siteID, gwID, ouID);
      })
      .then(response => {
        //表示内容をUnicode順に並び替え
        response['indoor-unit-informations'].sort(
          sortByUnicode('indoor-unit-name')
        );

        props.setOUInfo(response);
        props.setHasConfigGW(props.isBilling); //課金している場合のみ表示
        handleClose();
      })
      .catch(() => {
        setIsOpenErrorDialog(true);
      })
      .finally(() => {
        setAPIWait(false);
      });
  };
  /**
   * CANCELボタン押下時処理
   */
  const handleClose = () => {
    setIUName('');
    setIsOpenDialog(false);
  };

  const trans = useTranslation().t; //翻訳関数

  return (
    <>
      <AddButton
        onClick={handleClickOpen}
        id={'mv-btnAddIu'}
        buttonText={props.addButtonName}
      />
      {/* 登録台数が上限に達している場合は、追加付加のメッセージを表示 */}
      {(props.slType === 'new' &&
        iuNumber >= limit.REGISTRATION_NUM_NEW_SL_IU_MAX) ||
      (props.slType === 'old' &&
        iuNumber >= limit.REGISTRATION_NUM_OLD_SL_IU_MAX) ? (
        <NoMoreRegister
          open={isOpenDialog}
          setOpen={setIsOpenDialog}
          title={trans('NoMoreRegist.errorDlg.title')}
          text={trans('NoMoreRegist.errorDlg.msg')}
        />
      ) : (
        <>
          <Dialog
            open={isOpenDialog}
            onClose={handleClose}
            aria-labelledby="unit-add-dialog-title"
            id="addIuDialog">
            <DialogTitle id="unit-add-dialog-title">
              {trans('IUAddDialog.superlink.dialogBox.title')}
            </DialogTitle>
            <DialogContent>
              <DialogContentText>
                {trans('IUAddDialog.superlink.dialogBox.msg')}
              </DialogContentText>
              <Stack spacing={1.5}>
                {/* 室内機の名前 */}
                <ChkInputTextbox
                  setValue={setIUName}
                  setError={setIUNameError}
                  chkLength={format.BASIC_LENGTH_MAX}
                  type="text"
                  label={trans(
                    'IUAddDialog.superlink.dialogBox.txtField.label.unitName'
                  )}
                  value={iuName}
                  id="mv-inputIuName"
                  class="gwSetting__contents__textBox--unitName"
                  helperText=" "
                  errHelperText={trans(
                    'IUAddDialog.superlink.dialogBox.txtField.errHelperTxt'
                  )}
                  variant="standard"
                  autoFocus={true}
                  margin="dense"
                  chkInputProhibitedCharacter={format.BASIC_PATTERN_S3_POLICY}
                />
                {/* アドレス */}
                <SLIUAddressSelectBox
                  setAddress={setIUAddress}
                  label={trans(
                    'IUAddDialog.superlink.dialogBox.txtField.label.superlinkAddress'
                  )}
                  class="gwSetting__contents__textBox--address"
                  id="mv-selectIuUnit"
                />
                {/* 室内機タイプ */}
                <SLIUUnitTypeSelectBox setUnitType={setUnitType} />
              </Stack>
            </DialogContent>
            <DialogActions>
              <SecondaryButton
                disabled={apiWait}
                onClick={handleClose}
                buttonText={trans(
                  'IUAddDialog.superlink.dialogBox.buttonTxt.cancel'
                )}
                id={'mv-iuAddCancelBtn'}
              />
              <PrimaryButton
                onClick={addIU}
                buttonText={trans(
                  'IUAddDialog.superlink.dialogBox.buttonTxt.ok'
                )}
                id="mv-btnOkAddIu"
                disabled={okBtnDisabled}
                className="BtnOk"
                apiWait={apiWait}
              />
            </DialogActions>
          </Dialog>
          <APIErrorMessage
            open={isOpenErrorDialog}
            setOpen={setIsOpenErrorDialog}
            id={'mv-iuAddErrDlgBtn'}
          />
        </>
      )}
    </>
  );
};
export default IUAddDialog;
